/**
 * aiTargeting.js
 * ──────────────
 * Frontend-only mock implementation of the Enable Healthcare AI Lead-
 * Targeting engine described in `Enable_Healthcare_AI_Lead_Targeting_Spec.docx`
 * and the companion `Enable_Healthcare_Scoring_Matrix.xlsx`. The functions
 * here are pure — given a demo lead object, they produce a deterministic
 * product-fit ranking, campaign tags, talking points, and a mocked
 * "competitive analysis" + "AI enrichment" payload.
 *
 * The rules below faithfully reproduce the matrix:
 *   • base scores (EHR 60, PM 50, RCM 50, APCM 50, CCM 50, RPM 70, AWV 60)
 *   • specialty multipliers (Geriatrics → APCM 95, Cardiology → RPM 70 etc.)
 *   • EHR vendor bucketing (-40 strong incumbent, +20 weakening, +25 greenfield)
 *   • Medicare bene multipliers (≥800 → 1.15, 300-799 → 1.0)
 *   • age boosts (+5 ≥75, +10 ≥80)
 *   • operational modifiers (telehealth/eRx/PQRS/Medicare assignment)
 *   • CPT/HCPCS boosters when the lead's `top_hcpcs` codes match
 *
 * In production this would call out to a Claude/Azure agent. Here it
 * runs locally on synchronous pure functions, suitable for demo flow.
 */

export const PRODUCTS = ["EHR", "PM", "RCM", "APCM", "CCM", "RPM", "AWV"];

const BASE = { EHR: 60, PM: 50, RCM: 50, APCM: 50, CCM: 50, RPM: 70, AWV: 60 };

const SPECIALTY_RULES = {
  "Family Medicine":     { APCM: 90, CCM: 60, RPM: 60, AWV: 80 },
  "Internal Medicine":   { APCM: 80, CCM: 70, RPM: 60, AWV: 80 },
  "Geriatric Medicine":  { APCM: 95, CCM: 80, RPM: 70, AWV: 85 },
  Pediatrics:            { APCM: 30, CCM: 20, RPM: 20, AWV: 20 }, // low Medicare → demoted
  Cardiology:            { CCM: 70, RPM: 70, APCM: 0, AWV: 0 },
  "Pulmonology / Sleep": { CCM: 60, RPM: 60, APCM: 0, AWV: 0 },
  Pulmonology:           { CCM: 60, RPM: 60, APCM: 0, AWV: 0 },
  Endocrinology:         { CCM: 50, RPM: 50, APCM: 0, AWV: 0 },
  Nephrology:            { CCM: 60, RPM: 50, APCM: 0, AWV: 0 },
  Neurology:             { CCM: 50, RPM: 50, APCM: 0, AWV: 0 },
  Gastroenterology:      { CCM: 50, RPM: 45, APCM: 0, AWV: 0 },
  Rheumatology:          { CCM: 50, RPM: 45, APCM: 0, AWV: 0 },
};

// CMS hard gate: specialists are ineligible for APCM and AWV per the spec.
// These specialties never receive a non-zero score for APCM or AWV regardless
// of other signals — this is non-negotiable per the Specialty × Product Matrix.
const SPECIALIST_SPECIALTIES = [
  "Cardiology", "Pulmonology / Sleep", "Pulmonology", "Endocrinology",
  "Nephrology", "Neurology", "Gastroenterology", "Rheumatology",
  "Dermatology", "Oncology", "Orthopedics", "Urology", "OB/GYN",
  "Pain Management", "Interventional", "Radiology", "Ophthalmology",
];
function isSpecialist(specialty) {
  if (!specialty) return false;
  return SPECIALIST_SPECIALTIES.some((s) => specialty.toLowerCase().includes(s.toLowerCase()));
}
export function isAPCMEligible(lead) {
  return !isSpecialist(lead?.nppes?.specialty_group || lead?.specialty || "");
}

const STRONG_INCUMBENTS = [/epic/i, /cerner/i, /oracle/i, /athena/i, /HELP2/i];
const MID_TIER          = [/eclinical/i, /nextgen/i, /greenway/i, /allscripts/i, /veradigm/i];
const WEAKENING         = [/practice fusion/i, /aprima/i, /pulse/i, /mckesson/i, /practice partner/i, /ipatientcare/i, /emds/i, /curemd/i, /meridian/i, /meditech/i, /advanced data/i, /MyChartWriter/i, /HELP2/i];

const RPM_DRUG_DEVICES = [
  { rx: /eliquis|xarelto/i,                 dx: "Atrial fibrillation",   device: "ECG/Holter event monitor", w: 30 },
  { rx: /hydrochloro|losartan|atorvasta|lisinopril|amlodipine/i, dx: "Hypertension", device: "BP cuff", w: 30 },
  { rx: /furosemide|lasix/i,                dx: "Congestive heart failure", device: "Weight scale + BP cuff", w: 35 },
  { rx: /farxiga|glimepiride|metformin|jardiance|trulicity/i, dx: "Type 2 diabetes", device: "Glucometer / CGM", w: 30 },
  { rx: /arnuity|combivent|flovent|ventolin|spiriva/i, dx: "COPD / asthma", device: "Pulse oximeter", w: 35 },
  { rx: /escitalopram|duloxetine|sertraline|fluoxetine/i, dx: "Depression / anxiety", device: "(BHI add-on under APCM)", w: 15 },
];

const HCPCS_BOOSTS = {
  "99214": { CCM: 8, APCM: 6 },
  "99215": { CCM: 10, APCM: 8, RPM: 6 },
  "G0438": { AWV: 12 }, "G0439": { AWV: 12 },
  "G0444": { APCM: 8 }, "G0179": { APCM: 8, CCM: 6 },
  "G0556": { APCM: 14 }, "G0557": { APCM: 14 }, "G0558": { APCM: 14 },
  "99490": { CCM: 14 }, "99491": { CCM: 14 }, "99437": { CCM: 12 }, "99439": { CCM: 12 },
  "93000": { RPM: 10, CCM: 6 }, "93005": { RPM: 10, CCM: 6 }, "93010": { RPM: 10, CCM: 6 },
  "93306": { RPM: 12, CCM: 8 }, "93307": { RPM: 12, CCM: 8 }, "93308": { RPM: 12, CCM: 8 },
  "93229": { RPM: 14 }, "92928": { RPM: 8, CCM: 8 },
  "94010": { RPM: 12 }, "94060": { RPM: 12 }, "94070": { RPM: 12 },
  "82947": { RPM: 8, CCM: 6 }, "95250": { RPM: 14 }, "95251": { RPM: 14 },
  "99453": { RPM: 14 }, "99454": { RPM: 14 }, "99457": { RPM: 14 }, "99458": { RPM: 14 },
};

function applyEhrVendor(lead) {
  const vendor = lead.ehr?.vendor;
  if (!vendor) return { delta: 25, bucket: "GREENFIELD_FULL_STACK", note: "No certified EHR — greenfield install opportunity." };
  if (STRONG_INCUMBENTS.some((re) => re.test(vendor))) return { delta: -40, bucket: "COEXIST_LAYER", note: `${vendor} is a strong incumbent — pitch care-coord layer, not EHR replacement.` };
  if (WEAKENING.some((re) => re.test(vendor))) return { delta: 20, bucket: "FULL_STACK_REPLACE", note: `${vendor} is weakening / sunset-bound — strong replacement signal.` };
  if (MID_TIER.some((re) => re.test(vendor))) return { delta: -10, bucket: "FULL_STACK_REPLACE_IF_PAIN", note: `${vendor} is mid-tier — replace if pain signals; co-exist otherwise.` };
  return { delta: 0, bucket: "UNKNOWN", note: `${vendor} — bucketing inconclusive.` };
}

function operationalDelta(lead, product) {
  let d = 0;
  const sig = lead.signals || {};
  if (sig.medicare_assignment === false) return -100; // hard gate
  if (sig.medicare_assignment) d += 5;
  if (product === "EHR") {
    if (sig.telehealth) d += 5;
    if (sig.erx) d += 5;
    if (sig.pqrs) d += 5;
  }
  if (["APCM", "CCM", "RPM"].includes(product)) {
    if (sig.telehealth) d += 5;
    if (sig.pqrs) d += 5;
  }
  return d;
}

function demographicMultiplier(lead, product) {
  const benes = lead.medicare?.beneficiaries || 0;
  if (!["APCM", "CCM", "RPM", "AWV"].includes(product)) return 1;
  if (benes >= 800) return 1.15;
  if (benes >= 300) return 1.0;
  if (benes >= 100) return 0.85;
  return 0.55;
}

function ageBoost(lead) {
  const age = lead.medicare?.avg_age || 0;
  if (age >= 80) return 10;
  if (age >= 75) return 5;
  return 0;
}

function groupSizeMultiplier(lead, product) {
  const g = lead.practice?.group_size || 0;
  if (product !== "EHR") return 1;
  if (g >= 25) return 0.4; // route to AE
  if (g >= 11) return 0.6;
  if (g >= 1)  return 0.85;
  return 1;
}

function drugRpmFit(lead) {
  const drugs = (lead.top_drugs || []).map((d) => d.name || d).join(" ").toLowerCase();
  const hits = [];
  for (const r of RPM_DRUG_DEVICES) {
    if (r.rx.test(drugs)) hits.push(r);
  }
  return hits;
}

function hcpcsDeltas(lead) {
  const out = { EHR: 0, PM: 0, RCM: 0, APCM: 0, CCM: 0, RPM: 0, AWV: 0 };
  for (const h of lead.top_hcpcs || []) {
    const boost = HCPCS_BOOSTS[h.code];
    if (boost) for (const [p, v] of Object.entries(boost)) out[p] += v;
  }
  return out;
}

/**
 * scoreProducts(lead)
 * → { ranked: [{product, score, reasons:[]}], primary, secondary, ehr_bucket, drug_fits, gates: [] }
 */
export function scoreProducts(lead) {
  if (!lead) return { ranked: [], primary: null, secondary: null, ehr_bucket: null, drug_fits: [], gates: [] };
  const specRules = SPECIALTY_RULES[lead.nppes?.specialty_group] || {};
  const ehr = applyEhrVendor(lead);
  const drugFits = drugRpmFit(lead);
  const drugBoost = drugFits.reduce((s, d) => s + d.w, 0);
  const cpt = hcpcsDeltas(lead);
  const ageDelta = ageBoost(lead);
  const ranked = [];

  for (const p of PRODUCTS) {
    const reasons = [];
    let score = specRules[p] != null ? specRules[p] : BASE[p];
    reasons.push(`Base · ${specRules[p] != null ? `${lead.nppes?.specialty_group} → ${specRules[p]}` : `${BASE[p]}`}`);

    if (p === "EHR") {
      score += ehr.delta;
      reasons.push(`EHR vendor · ${ehr.bucket} (${ehr.delta >= 0 ? "+" : ""}${ehr.delta})`);
    }
    if (["APCM", "CCM", "RPM"].includes(p) && drugBoost > 0) {
      score += drugBoost;
      reasons.push(`Drug-disease signals · +${drugBoost} (${drugFits.map((d) => d.dx).join(", ")})`);
    }
    if (cpt[p]) {
      score += cpt[p];
      reasons.push(`CPT/HCPCS workflow · +${cpt[p]}`);
    }
    if (ageDelta && ["APCM", "CCM", "RPM", "AWV"].includes(p)) {
      score += ageDelta;
      reasons.push(`Avg age boost · +${ageDelta}`);
    }
    const op = operationalDelta(lead, p);
    if (op === -100) {
      score = 0;
      reasons.push("Hard-gated: no Medicare assignment.");
    } else if (op !== 0) {
      score += op;
      reasons.push(`Operational signals · ${op >= 0 ? "+" : ""}${op}`);
    }

    // CMS rule: Pediatrics has no Medicare → cap APCM/CCM/RPM/AWV
    if (lead.nppes?.specialty_group === "Pediatrics" && ["APCM", "CCM", "RPM", "AWV"].includes(p)) {
      score = Math.min(score, 35);
      reasons.push("Pediatrics cap (Medicare-light).");
    }

    const benesMult = demographicMultiplier(lead, p);
    const groupMult = groupSizeMultiplier(lead, p);
    if (benesMult !== 1) {
      score = Math.round(score * benesMult);
      reasons.push(`Medicare benes multiplier · ×${benesMult} (${lead.medicare?.beneficiaries?.toLocaleString() || "—"})`);
    }
    if (groupMult !== 1) {
      score = Math.round(score * groupMult);
      reasons.push(`Group-size multiplier · ×${groupMult} (${lead.practice?.group_size || "—"} FTE)`);
    }

    // CMS hard gate — APCM/AWV are zero for specialist specialties (non-negotiable per spec)
    if ((p === "APCM" || p === "AWV") && isSpecialist(lead.nppes?.specialty_group || lead.specialty || "")) {
      score = 0;
      reasons.length = 0;
      reasons.push(`CMS hard gate · ${p} restricted to primary care providers — score forced to 0.`);
    }

    ranked.push({ product: p, score: Math.max(0, Math.min(100, Math.round(score))), reasons });
  }

  ranked.sort((a, b) => b.score - a.score);

  // CCM-first strategy (per Specialty × Product Matrix) — when CCM ≥ 50 and the
  // specialty qualifies for CCM, CCM is the primary recommendation, never APCM.
  // APCM is positioned as overflow/secondary for PCP specialties only.
  const ccm = ranked.find((r) => r.product === "CCM");
  const apcm = ranked.find((r) => r.product === "APCM");
  if (ccm && ccm.score >= 50) {
    // Pull CCM to top regardless of raw rank
    const idx = ranked.indexOf(ccm);
    if (idx > 0) { ranked.splice(idx, 1); ranked.unshift(ccm); ccm.reasons.push("CCM-first strategy · pulled to primary."); }
  }

  // CMS rule: APCM and CCM cannot stack — pick the higher one only if both top-3
  let primary = ranked[0];
  let secondary = ranked.find((r) => r.product !== primary.product) || null;
  if (primary && secondary && ((primary.product === "APCM" && secondary.product === "CCM") || (primary.product === "CCM" && secondary.product === "APCM"))) {
    secondary = ranked.find((r) => r.product !== primary.product && r.product !== secondary.product) || null;
  }
  // If both CCM and APCM scored, ensure APCM is labeled overflow (not co-primary)
  const apcmOverflow = !!(ccm && apcm && ccm.score >= 50 && apcm.score >= 50);

  return {
    ranked, primary, secondary,
    ehr_bucket: ehr,
    drug_fits: drugFits,
    gates: ranked.some((r) => r.score === 0) ? ["medicare_assignment"] : [],
    apcm_eligible: !isSpecialist(lead.nppes?.specialty_group || lead.specialty || ""),
    apcm_overflow: apcmOverflow,
    ccm_first_applied: !!(ccm && ccm.score >= 50),
  };
}

/* ── Difficulty class per product ───────────────────────── */
export function difficultyClass(productCode) {
  if (productCode === "AWV") return { id: "easy", label: "Easy", note: "14–30 day decision; lightest lift, often the wedge." };
  if (productCode === "RPM") return { id: "easy", label: "Easy", note: "45–75 day cycle; new revenue + device service." };
  if (productCode === "CCM" || productCode === "APCM") return { id: "easy", label: "Easy", note: "30–60 day cycle; new revenue motion." };
  if (productCode === "RCM") return { id: "medium", label: "Medium", note: "Often standalone; faster than EHR switch." };
  if (productCode === "PM") return { id: "medium", label: "Medium", note: "Bundled with EHR/RCM, 30–90 day cycle." };
  if (productCode === "EHR") return { id: "hard", label: "Hard", note: "Replacement / new install, 60–120 day cycle." };
  return { id: "medium", label: "Medium" };
}

/* ── Per-lead talking points ────────────────────────────── */
export function generateTalkingPoints(lead, scoring) {
  const out = [];
  if (!scoring) return out;
  const { primary, ehr_bucket, drug_fits } = scoring;
  if (primary) {
    if (primary.product === "RPM") out.push(`Lead with **${primary.product}**: ${drug_fits[0]?.device || "device-based monitoring"} for ${drug_fits[0]?.dx || lead.nppes?.specialty_group} workflows. Cite ${lead.medicare?.beneficiaries?.toLocaleString() || "—"} Medicare benes as the volume hook.`);
    else if (primary.product === "APCM") out.push(`Lead with **APCM** (G0556/G0557/G0558). $98–$117/mo per Medicare patient × ${lead.medicare?.beneficiaries?.toLocaleString() || "—"} benes ≈ ${Math.round(((lead.medicare?.beneficiaries || 0) * 98 * 12) / 1000)}K/yr standalone revenue.`);
    else if (primary.product === "CCM") out.push(`Lead with **CCM** (99490/99491/99437/99439). Stack with RPM for chronic-disease specialties.`);
    else if (primary.product === "AWV") out.push(`Lead with **AWV** (G0438/G0439). Lightest-lift wedge — 14–30 day to live.`);
    else if (primary.product === "EHR") out.push(`Lead with **EHR replacement** — ${ehr_bucket?.note}`);
  }
  if (ehr_bucket?.bucket === "GREENFIELD_FULL_STACK") out.push(`Position as a **greenfield install**: 35–47 day go-live, no migration cost.`);
  if (ehr_bucket?.bucket === "FULL_STACK_REPLACE") out.push(`Cite the **${lead.ehr?.vendor} sunset risk** — practices like theirs face support-cliff inside 18 months.`);
  if (ehr_bucket?.bucket === "COEXIST_LAYER") out.push(`Do **not** pitch EHR replacement — they're on a strong incumbent. Lead the call with care-coord (APCM/CCM/RPM) layer.`);
  for (const d of drug_fits.slice(0, 2)) out.push(`Drug signal: **${d.dx}** detected → ${d.device} fit.`);
  if ((lead.medicare?.avg_age || 0) >= 80) out.push(`**Avg age ≥ 80** — chronic disease density is exceptional; lean into APCM/CCM revenue math.`);
  return out;
}

/* ── Competitive analysis (mocked) ──────────────────────── */
export function competitiveAnalysis(lead, scoring) {
  if (!scoring) return null;
  const ehr = lead.ehr?.vendor;
  const compsByVendor = {
    Epic: { incumbent: "Epic Systems", lossRate: 0.18, displaceMotion: "Care-coordination layer — don't replace Epic, layer APCM/CCM/RPM alongside.", peers: ["Northwell Health Cardiology", "UC Davis Heart"] },
    Cerner: { incumbent: "Cerner / Oracle Health", lossRate: 0.21, displaceMotion: "Care-coordination layer — Cerner ambulatory has a long sales cycle; layer don't replace.", peers: ["Indiana Cardiology Associates", "Fort Wayne Heart Group"] },
    "Practice Fusion": { incumbent: "Practice Fusion", lossRate: 0.62, displaceMotion: "Full-stack replacement — DOJ settlement, no roadmap, high churn.", peers: ["Allen County Cardiology (replaced 2024)", "Charles Paolino MD"] },
    eClinicalWorks: { incumbent: "eClinicalWorks", lossRate: 0.34, displaceMotion: "Replace if billing pain is present; otherwise co-exist with care-coord bundle.", peers: ["Athens Heart Center", "Desert Pulmonary"] },
    Athena: { incumbent: "athenahealth", lossRate: 0.16, displaceMotion: "Care-coordination layer — athenahealth is sticky; pitch alongside, not against.", peers: ["Chelmsford Primary"] },
  };
  const matched = ehr && Object.keys(compsByVendor).find((k) => new RegExp(k, "i").test(ehr));
  const comp = matched ? compsByVendor[matched] : { incumbent: "No certified EHR", lossRate: 0.81, displaceMotion: "Greenfield full-stack install — no migration cost, 35–47 day go-live.", peers: ["Aalfa Family Clinic (greenfield 2024)", "American Family Care"] };
  const winRate = Math.round((1 - comp.lossRate) * 100);
  return {
    incumbent: comp.incumbent,
    expectedWinRate: winRate,
    motion: comp.displaceMotion,
    peers: comp.peers,
    pricingFloor: scoring.primary?.product === "EHR" ? "$48K/yr" : scoring.primary?.product === "APCM" ? "$24K/yr" : scoring.primary?.product === "RPM" ? "$18K/yr" : "—",
    objections: [
      ehr ? `"We're already on ${ehr}, what's the migration cost?"` : `"We don't have an EHR — what's the cheapest entry point?"`,
      `"How long until we're live?" — ${scoring.primary ? difficultyClass(scoring.primary.product).note : "varies by module"}`,
      `"What's the ROI?" — ${(lead.medicare?.beneficiaries || 0) >= 300 ? `${lead.medicare?.beneficiaries?.toLocaleString()} benes × ~$98/mo APCM = $${Math.round(((lead.medicare?.beneficiaries || 0) * 98 * 12) / 1000)}K/yr standalone` : "explore stacking AWV + RPM for revenue lift"}`,
    ],
  };
}

/* ── Cohort campaign recommender (group leads → segments) */
export const CAMPAIGN_PLAYBOOK = [
  { id: "C-01", name: "Hypertension RPM Wedge",       targets: ["RPM"], filter: (l, s) => s.drug_fits?.some((d) => /Hypertension/i.test(d.dx)) },
  { id: "C-02", name: "QMB-Eligible APCM (G0558)",    targets: ["APCM"], filter: (l, s) => s.primary?.product === "APCM" && (l.medicare?.dual_eligibles || 0) >= 50 },
  { id: "C-03", name: "CHF / Cardiac Care Stack",     targets: ["RPM", "CCM"], filter: (l, s) => s.drug_fits?.some((d) => /heart failure|atrial|hypertension/i.test(d.dx)) || l.nppes?.specialty_group === "Cardiology" },
  { id: "C-04", name: "Pulmonary RPM (Pulse-Ox)",     targets: ["RPM"], filter: (l, s) => /Pulmonology/i.test(l.nppes?.specialty_group || "") || s.drug_fits?.some((d) => /COPD/i.test(d.dx)) },
  { id: "C-05", name: "Diabetes Control Stack",       targets: ["RPM", "CCM", "APCM"], filter: (l, s) => s.drug_fits?.some((d) => /diabetes/i.test(d.dx)) },
  { id: "C-09", name: "Geri-Heavy AWV / APCM",         targets: ["AWV", "APCM"], filter: (l) => (l.medicare?.avg_age || 0) >= 78 && (l.medicare?.beneficiaries || 0) >= 300 },
  { id: "C-10", name: "AWV Wedge for FM/IM",           targets: ["AWV"], filter: (l) => /Family Medicine|Internal Medicine/i.test(l.nppes?.specialty_group || "") },
  { id: "C-11", name: "BHI Add-On (G0568)",            targets: ["APCM"], filter: (l, s) => s.drug_fits?.some((d) => /Depression/i.test(d.dx)) },
  { id: "C-12", name: "APCM + RPM Bundle",             targets: ["APCM", "RPM"], filter: (l, s) => s.primary?.product === "APCM" && s.ranked.find((r) => r.product === "RPM" && r.score >= 70) },
  { id: "C-13", name: "EHR Replacement (Sunset Risk)", targets: ["EHR"], filter: (l, s) => s.ehr_bucket?.bucket === "FULL_STACK_REPLACE" },
  { id: "C-14", name: "Greenfield EHR + APCM",         targets: ["EHR", "APCM"], filter: (l, s) => s.ehr_bucket?.bucket === "GREENFIELD_FULL_STACK" && /Family Medicine|Internal Medicine/i.test(l.nppes?.specialty_group || "") },
];

export function recommendCampaigns(leads) {
  const out = [];
  for (const c of CAMPAIGN_PLAYBOOK) {
    const matched = leads.filter((l) => {
      try { return c.filter(l, scoreProducts(l)); } catch { return false; }
    });
    if (matched.length === 0) continue;
    const totalBenes = matched.reduce((s, l) => s + (l.medicare?.beneficiaries || 0), 0);
    const projectedArr = c.targets.includes("APCM") ? Math.round(totalBenes * 98 * 12) : c.targets.includes("RPM") ? Math.round(totalBenes * 65 * 12) : c.targets.includes("CCM") ? Math.round(totalBenes * 64 * 12) : c.targets.includes("AWV") ? Math.round(totalBenes * 175) : matched.length * 50000;
    out.push({
      id: c.id, name: c.name, targets: c.targets,
      leads: matched.map((l) => ({ id: l.id, clinic: l.clinic, npi: l.npi })),
      count: matched.length, totalBenes, projectedArr,
      difficulty: difficultyClass(c.targets[0]).label,
    });
  }
  return out.sort((a, b) => b.projectedArr - a.projectedArr);
}

/* ── Mock AI-Enrich (crawls the internet — fake) ─────────── */
export function aiEnrich(lead) {
  const findings = [];
  const baseFindings = [
    { source: "Google Business Profile",  field: "website",        old: lead.practice?.web || "—", new: `${lead.clinic.toLowerCase().replace(/[^a-z]+/g, "")}.health`, confidence: 0.94 },
    { source: "NPPES Provider Registry",  field: "credential",     old: lead.nppes?.credential, new: lead.nppes?.credential, confidence: 1.0, note: "Verified" },
    { source: "CMS Public Use File 2024", field: "medicare_benes", old: lead.medicare?.beneficiaries, new: lead.medicare?.beneficiaries + Math.round((Math.random() - 0.3) * 30), confidence: 0.97 },
    { source: "ONC CHPL",                 field: "ehr_vendor",     old: lead.ehr?.vendor || "(none)", new: lead.ehr?.vendor || "(no certified record)", confidence: lead.ehr ? 1.0 : 0.92, note: lead.ehr ? "Verified" : "Confirmed greenfield" },
    { source: "LinkedIn / Google Search", field: "practice_manager", old: "—", new: ["Maria Gomez, RN", "James Patel, BSN", "Sarah Chen, MBA"][Math.floor(Math.random() * 3)], confidence: 0.78, note: "New finding" },
    { source: "Google News Search",       field: "recent_news",    old: "—", new: ["Practice expanded by 2 PAs Q4 2025 (LinkedIn)", "Local press: opening 2nd location March 2026", "Joined regional ACO in Jan 2026"][Math.floor(Math.random() * 3)], confidence: 0.71, note: "Signal worth mentioning" },
    { source: "MIPS Public Reporting",    field: "mips_score",     old: "—", new: `${75 + Math.floor(Math.random() * 20)}/100 (above peer median)`, confidence: 0.88, note: "Confirms PQRS attestation" },
  ];
  return {
    crawledAt: new Date().toISOString(),
    sources: ["Google", "NPPES", "CMS PUF", "ONC CHPL", "LinkedIn", "MIPS Public Reporting"],
    findings: baseFindings,
    summary: `Crawled 6 public sources in 4.2s. ${baseFindings.filter((f) => f.note?.includes("New") || f.note?.includes("worth")).length} new signals found, ${baseFindings.filter((f) => f.note?.includes("Verified") || f.note?.includes("Confirmed")).length} verifications.`,
    durationMs: 4200,
  };
}


/* ── Vendor bucket classifier (exposed) ─────────────────── */
export function vendorBucket(vendorName) {
  if (!vendorName) return { id: "NO_EHR", label: "No EHR", strategy: "GREENFIELD_FULL_STACK", talking_point: "Greenfield install — no migration cost, 35–47 day go-live.", tone: "green" };
  if (STRONG_INCUMBENTS.some((re) => re.test(vendorName))) return { id: "STRONG_INCUMBENT", label: "Strong incumbent", strategy: "COEXIST_LAYER", talking_point: `${vendorName} is a strong incumbent — pitch care-coord layer, not EHR replacement.`, tone: "blue" };
  if (WEAKENING.some((re) => re.test(vendorName))) {
    const dojNote = /practice fusion/i.test(vendorName) ? " — DOJ 2020 settlement, switch-ready" : "";
    return { id: "WEAKENING_LEGACY", label: "Legacy / sunset", strategy: "FULL_STACK_REPLACE", talking_point: `${vendorName} is weakening / sunset-bound${dojNote}.`, tone: "amber" };
  }
  if (MID_TIER.some((re) => re.test(vendorName))) {
    const ecwNote = /eclinical/i.test(vendorName) ? "  $155M DOJ CIA removes the financial barrier to switching." : "";
    return { id: "MID_TIER", label: "Mid-tier, replaceable", strategy: "FULL_STACK_REPLACE_IF_PAIN", talking_point: `${vendorName} mid-tier — replace if billing pain is present.${ecwNote}`, tone: "purple" };
  }
  return { id: "UNKNOWN", label: "Unbucketed", strategy: "DISCOVERY", talking_point: `${vendorName} — needs discovery to classify.`, tone: "blue" };
}

/* ── CMS 2026 monthly per-patient rates (mock $) ────────── */
export const CMS_2026_RATES = {
  CCM:  { code: "99490", per_pt_per_mo: 64,  enrollment_assumption: 0.60, label: "CCM 99490" },
  APCM: { code: "G0556/G0557/G0558", per_pt_per_mo: 98, dual_eligible_per_pt_per_mo: 117, enrollment_assumption: 0.45, label: "APCM G0556+" },
  RPM:  { code: "99454/99457", per_pt_per_mo: 65, enrollment_assumption: 0.50, label: "RPM 99454/99457" },
  AWV:  { code: "G0438/G0439", per_visit: 175, capture_assumption: 0.70, label: "AWV G0438" },
};

/* ── Specialty cluster labelizer ────────────────────────── */
function specialtyCluster(specialty) {
  if (!specialty) return "Other";
  const s = specialty.toLowerCase();
  if (s.includes("cardio")) return "Cardiology";
  if (s.includes("pulmon") || s.includes("sleep")) return "Pulmonology";
  if (s.includes("nephro")) return "Nephrology";
  if (s.includes("endocr")) return "Endocrinology";
  if (s.includes("neuro")) return "Neurology";
  if (s.includes("gastro")) return "Gastroenterology";
  if (s.includes("rheumat")) return "Rheumatology";
  if (s.includes("family")) return "Family Medicine";
  if (s.includes("internal")) return "Internal Medicine";
  if (s.includes("geriatr")) return "Geriatric Medicine";
  if (s.includes("pediatr")) return "Pediatrics";
  return specialty;
}

/* ── 8 starter AI segments (auto-generated) ─────────────── */
export function generateAISegments(leads) {
  if (!Array.isArray(leads) || leads.length === 0) return [];

  const scored = leads.map((l) => {
    let sc = null;
    try { sc = scoreProducts(l); } catch {}
    return { lead: l, sc, vendor: vendorBucket(l.ehr?.vendor) };
  });

  const drugRe = /arnuity|combivent|flovent|ventolin|spiriva|trelegy|advair|symbicort/i;
  const cptRe = /^(940[1-7]0)$/;

  const has = (s, p, min) => (s.sc?.ranked || []).some((r) => r.product === p && r.score >= min);
  const primaryIs = (s, p) => s.sc?.primary?.product === p;
  const specialtyMatches = (s, list) => {
    const sp = (s.lead.nppes?.specialty_group || s.lead.specialty || "").toLowerCase();
    return list.some((x) => sp.includes(x.toLowerCase()));
  };

  const segs = [
    {
      id: "ai-ccm-cardio",
      name: "CCM — Cardiology (strong fit)",
      primary: "CCM", secondary: "RPM", apcm_eligible: false,
      tagline: "CHF + HTN + AFib panel → CCM 99490 + RPM 99454/99457 stack",
      filter: (s) => specialtyMatches(s, ["cardio"]) && has(s, "CCM", 60),
    },
    {
      id: "ai-ccm-pulmo",
      name: "CCM — Pulmonology (strong fit)",
      primary: "CCM", secondary: "RPM", apcm_eligible: false,
      tagline: "Pulse-ox confirmed by COPD inhaler signals (Spiriva, Trelegy, Symbicort)",
      filter: (s) => specialtyMatches(s, ["pulmon", "sleep"]) && has(s, "CCM", 50),
    },
    {
      id: "ai-ccm-rpm-im",
      name: "CCM + RPM — Internal Medicine (dual-device)",
      primary: "CCM", secondary: "RPM", apcm_eligible: true,
      tagline: "PCP-eligible · APCM as overflow on patients <20 min CCM",
      filter: (s) => specialtyMatches(s, ["internal medicine"]) && has(s, "CCM", 50),
    },
    {
      id: "ai-rpm-copd",
      name: "RPM pulse-ox — COPD confirmed",
      primary: "RPM", secondary: "CCM", apcm_eligible: false,
      tagline: "Drug-signal-first segment — qualifying COPD inhaler detected",
      filter: (s) => {
        const drugs = (s.lead.top_drugs || []).map((d) => (d?.name || d || "").toString());
        const cpts = (s.lead.top_hcpcs || []).map((d) => (d?.code || d || "").toString());
        return drugs.some((d) => drugRe.test(d)) || cpts.some((c) => cptRe.test(c));
      },
    },
    {
      id: "ai-ehr-legacy",
      name: "EHR — Legacy / stranded platform",
      primary: "EHR", secondary: "RCM", apcm_eligible: undefined,
      tagline: "Practice Fusion · Aprima · McKesson · iPatientCare · eMDs · CureMD — switch-ready",
      filter: (s) => s.vendor.id === "WEAKENING_LEGACY",
    },
    {
      id: "ai-rcm-denial",
      name: "RCM — Denial-prone specialties",
      primary: "RCM", secondary: "EHR", apcm_eligible: undefined,
      tagline: "Cardiology · OB/GYN · Pain Mgmt · Nephrology · Interventional + FL/NY/NJ/MD modifier",
      filter: (s) => specialtyMatches(s, ["cardio", "ob/gyn", "pain", "nephro", "interventional"]) || ["FL", "NY", "NJ", "MD"].includes(s.lead.state || s.lead.practice?.state),
    },
    {
      id: "ai-apcm-overflow",
      name: "APCM overflow — PCP Medicare 300+",
      primary: "APCM", secondary: "CCM", apcm_eligible: true, apcm_overflow: true,
      tagline: "Family/Internal/Geriatric only · benes ≥ 300 · CCM-anchor practices · G0558 dual-eligible $117/mo",
      filter: (s) => specialtyMatches(s, ["family medicine", "internal medicine", "geriatric"]) && (s.lead.medicare?.beneficiaries || 0) >= 300 && has(s, "APCM", 50),
    },
    {
      id: "ai-awv-fmim",
      name: "AWV wedge — FM/IM low-capture",
      primary: "AWV", secondary: "APCM", apcm_eligible: true,
      tagline: "Foot-in-the-door product (14–30 day cycle) · pairs with APCM as initiating visit",
      filter: (s) => specialtyMatches(s, ["family medicine", "internal medicine"]) && has(s, "AWV", 50) && (s.lead.medicare?.beneficiaries || 0) >= 200,
    },
  ];

  return segs.map((seg) => {
    const matched = scored.filter(seg.filter).map((s) => s.lead);
    return { ...seg, kind: "ai", count: matched.length, leads: matched };
  });
}

/* ── Aggregate scoring across a segment ─────────────────── */
export function aggregateSegment(leads) {
  if (!leads?.length) return null;
  const productScores = {}; // product → array of scores
  const vendorCounts = {};
  const drugCounts = {};
  const stateCounts = {};
  let totalBenes = 0, dualEligibleTotal = 0, telehealthY = 0, telehealthTotal = 0;
  const ehrBuckets = { STRONG_INCUMBENT: 0, MID_TIER: 0, WEAKENING_LEGACY: 0, NO_EHR: 0, UNKNOWN: 0 };
  let apcmIneligibleCount = 0;
  for (const l of leads) {
    let sc = null;
    try { sc = scoreProducts(l); } catch {}
    if (sc?.ranked) {
      sc.ranked.forEach((r) => { (productScores[r.product] = productScores[r.product] || []).push(r.score); });
    }
    const v = vendorBucket(l.ehr?.vendor);
    ehrBuckets[v.id] = (ehrBuckets[v.id] || 0) + 1;
    vendorCounts[l.ehr?.vendor || "(none)"] = (vendorCounts[l.ehr?.vendor || "(none)"] || 0) + 1;
    (l.top_drugs || []).forEach((d) => { const n = d?.name || d; if (n) drugCounts[n] = (drugCounts[n] || 0) + 1; });
    const st = l.state || l.practice?.state; if (st) stateCounts[st] = (stateCounts[st] || 0) + 1;
    totalBenes += (l.medicare?.beneficiaries || 0);
    dualEligibleTotal += (l.medicare?.dual_eligibles || 0);
    if (l.signals?.telehealth) telehealthY += 1;
    telehealthTotal += 1;
    if (!sc?.apcm_eligible) apcmIneligibleCount += 1;
  }
  const productAvg = {};
  Object.keys(productScores).forEach((p) => {
    const arr = productScores[p];
    productAvg[p] = Math.round(arr.reduce((s, x) => s + x, 0) / arr.length);
  });
  const ranked = Object.entries(productAvg)
    .map(([product, score]) => ({ product, score, primaryFor: 0 }))
    .sort((a, b) => b.score - a.score);
  // primary count per product (for "X/N practices" copy)
  leads.forEach((l) => {
    try { const sc = scoreProducts(l); if (sc?.primary) {
      const r = ranked.find((x) => x.product === sc.primary.product);
      if (r) r.primaryFor += 1;
    } } catch {}
  });
  return {
    ranked, productAvg, vendorCounts, drugCounts, stateCounts, ehrBuckets,
    totalBenes, dualEligibleTotal,
    telehealthPct: telehealthTotal ? Math.round((telehealthY / telehealthTotal) * 100) : 0,
    apcmIneligibleCount,
    leadCount: leads.length,
    topDrugs: Object.entries(drugCounts).sort((a, b) => b[1] - a[1]).slice(0, 3),
    topStates: Object.entries(stateCounts).sort((a, b) => b[1] - a[1]).slice(0, 3),
    topVendors: Object.entries(vendorCounts).sort((a, b) => b[1] - a[1]).slice(0, 2),
  };
}
